import React, { useState, useEffect } from 'react';
import { RefreshCw, Activity, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { getAggregatedMetrics, getApiMetrics } from '../../services/apiMetricsService';
import ProviderPerformanceCard from './components/ProviderPerformanceCard';
import RetryPatternChart from './components/RetryPatternChart';
import ResponseTimeChart from './components/ResponseTimeChart';
import ErrorAnalysisCard from './components/ErrorAnalysisCard';

const ApiMonitoringDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [rawMetrics, setRawMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '1h':
          startDate?.setHours(startDate?.getHours() - 1);
          break;
        case '24h':
          startDate?.setHours(startDate?.getHours() - 24);
          break;
        case '7d':
          startDate?.setDate(startDate?.getDate() - 7);
          break;
        case '30d':
          startDate?.setDate(startDate?.getDate() - 30);
          break;
        default:
          startDate?.setHours(startDate?.getHours() - 24);
      }

      const filters = {
        startDate,
        endDate,
        provider: selectedProvider !== 'all' ? selectedProvider : null,
        limit: 1000
      };

      const [aggregated, raw] = await Promise.all([
        getAggregatedMetrics(filters),
        getApiMetrics(filters)
      ]);

      setMetrics(aggregated);
      setRawMetrics(raw);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [timeRange, selectedProvider]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchMetrics();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange, selectedProvider]);

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSuccessRateIcon = (rate) => {
    if (rate >= 95) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (rate >= 85) return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    return <AlertCircle className="w-5 h-5 text-red-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">API Monitoring Dashboard</h1>
        <p className="text-gray-600">Real-time tracking of OpenAI and Perplexity API performance, retry patterns, and failure rates</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap items-center gap-4">
        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-500" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>

        {/* Provider Filter */}
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-gray-500" />
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Providers</option>
            <option value="openai">OpenAI</option>
            <option value="perplexity">Perplexity</option>
          </select>
        </div>

        {/* Auto Refresh Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e?.target?.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm text-gray-700">Auto-refresh (30s)</span>
        </label>

        {/* Manual Refresh */}
        <button
          onClick={fetchMetrics}
          disabled={loading}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && !metrics ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : metrics ? (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Total Calls */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Total API Calls</h3>
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.totalCalls?.toLocaleString()}</p>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="text-green-600">{metrics?.successfulCalls} successful</span>
                <span className="text-gray-400">•</span>
                <span className="text-red-600">{metrics?.failedCalls} failed</span>
              </div>
            </div>

            {/* Success Rate */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Success Rate</h3>
                {getSuccessRateIcon(parseFloat(metrics?.successRate))}
              </div>
              <p className={`text-3xl font-bold ${getSuccessRateColor(parseFloat(metrics?.successRate))}`}>
                {metrics?.successRate}%
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {metrics?.successfulCalls} of {metrics?.totalCalls} calls succeeded
              </p>
            </div>

            {/* Average Response Time */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Avg Response Time</h3>
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.averageResponseTime}ms</p>
              <p className="mt-2 text-sm text-gray-500">
                Across all providers
              </p>
            </div>

            {/* Average Retry Count */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Avg Retry Count</h3>
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.averageRetryCount}</p>
              <p className="mt-2 text-sm text-gray-500">
                Retries before success/failure
              </p>
            </div>
          </div>

          {/* Provider Performance Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {Object?.entries(metrics?.byProvider || {})?.map(([provider, stats]) => (
              <ProviderPerformanceCard key={provider} provider={provider} stats={stats} />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Retry Pattern Analytics */}
            <RetryPatternChart 
              retryDistribution={metrics?.retryDistribution} 
              totalCalls={metrics?.totalCalls}
            />

            {/* Response Time Visualization */}
            <ResponseTimeChart 
              rawMetrics={rawMetrics}
              timeRange={timeRange}
            />
          </div>

          {/* Error Analysis */}
          <ErrorAnalysisCard 
            errorTypes={metrics?.errorTypes}
            failedCalls={metrics?.failedCalls}
            rawMetrics={rawMetrics}
          />
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">No API calls have been tracked yet. Start using the agents to see metrics.</p>
        </div>
      )}
    </div>
  );
};

export default ApiMonitoringDashboard;